import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ExternalLink, QrCode, Lock, Unlock, Inbox, ArrowRight, RotateCcw, Check, Smartphone, Building2, FileCode2 } from "lucide-react";
import { api, priceLabel } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { VinconexLogo } from "@/components/VinconexMark";
import { LegalLinks } from "@/components/LegalLinks";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const STEPS = [
  { icon: QrCode, title: "Scan the sticker", body: "Point your phone at the code, or drive the page in the frame — it's the real page a shopper gets off the windshield." },
  { icon: Lock, title: "Ask for the price", body: "Tap the locked price. Leave any name and mobile number — that's the whole trade a shopper makes." },
  { icon: Unlock, title: "See what opens up", body: "The lower price, the reconditioning report, days on the lot, how the store got the car, the as-is estimate." },
  { icon: Inbox, title: "Watch it hit the CRM", body: "Your lead shows up below in seconds — with the car, the price shown, and the rooftop it belongs to." },
];

function sessionId() {
  const key = "vx-tour-session";
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`).slice(0, 36);
    sessionStorage.setItem(key, id);
  }
  return id;
}

function LeadReveal({ lead }) {
  const [xml, setXml] = useState(false);
  if (!lead) {
    return (
      <div className="rounded-xl border border-dashed border-[#2A2E3D] bg-[#0B0D14] p-6 text-center" data-testid="tour-lead-waiting">
        <Inbox className="h-7 w-7 mx-auto mb-3 text-slate-600" />
        <p className="text-sm text-slate-400">Your lead lands here the second you unlock a price in the demo.
        <span className="block text-slate-600 mt-1">Nothing is sent to a real dealership — these are Vinconex's own demo cars.</span></p>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-emerald-800/50 bg-emerald-950/10 p-5 animate-fade-up" data-testid="tour-lead-card">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-400 font-mono-tech">
        <Check className="h-3.5 w-3.5" /> Lead delivered {lead.crm_status === "sent" ? "to the CRM inbox" : `· CRM ${lead.crm_status || "queued"}`}
      </div>
      <div className="font-heading text-xl font-extrabold mt-2" data-testid="tour-lead-name">{lead.customer_name}</div>
      <div className="text-sm text-slate-300">{lead.phone || lead.email}</div>
      <div className="mt-3 space-y-1.5 text-sm">
        <div className="flex gap-2"><span className="text-slate-500 w-28 shrink-0">Vehicle</span><span className="text-slate-200">{lead.vehicle_label || "—"}</span></div>
        {lead.unlock_label && <div className="flex gap-2"><span className="text-slate-500 w-28 shrink-0">Unlocked</span><span className="text-slate-200">{lead.unlock_label}{lead.unlock_price ? ` · ${priceLabel(lead.unlock_price)} shown` : ""}</span></div>}
        <div className="flex gap-2"><span className="text-slate-500 w-28 shrink-0">Routed to</span><span className="text-slate-200" data-testid="tour-lead-store">{lead.store_name || "group inbox"}</span></div>
        {(lead.comment_summary || lead.comment) && <div className="flex gap-2"><span className="text-slate-500 w-28 shrink-0">Their note</span><span className="text-slate-200">{lead.comment_summary || lead.comment}</span></div>}
      </div>
      {lead.adf_xml && (
        <>
          <button type="button" onClick={() => setXml((x) => !x)} data-testid="tour-adf-toggle" className="mt-4 text-xs text-[#7FC3FF] hover:text-white inline-flex items-center gap-1.5">
            <FileCode2 className="h-3.5 w-3.5" /> {xml ? "Hide" : "Show"} the ADF your CRM receives
          </button>
          {xml && <pre className="mt-2 max-h-64 overflow-auto rounded-lg border border-[#1C2233] bg-black/60 p-3 text-[10px] leading-relaxed text-slate-400" data-testid="tour-adf-xml">{lead.adf_xml}</pre>}
        </>
      )}
    </div>
  );
}

export default function TryDemo() {
  const { token = "" } = useParams();
  const [params] = useSearchParams();
  const ref = params.get("ref") || "";
  const sid = useMemo(() => sessionId(), []);
  const [brand, setBrand] = useState(null);
  const [state, setState] = useState(null);
  const [lead, setLead] = useState(null);
  const [since, setSince] = useState(() => new Date().toISOString());
  const [frameKey, setFrameKey] = useState(0);
  const tracked = useRef(new Set());

  const track = useCallback((step) => {
    if (tracked.current.has(step)) return;
    tracked.current.add(step);
    api.post("/public/demo/tour", { session_id: sid, step, ref, token }).catch(() => {});
  }, [ref, token, sid]);

  useEffect(() => {
    api.get("/public/demo").then(({ data }) => setState(data)).catch(() => toast.error("The demo store is warming up — try again in a moment"));
    if (token) api.get(`/public/demo/invite/${token}`).then(({ data }) => setBrand(data)).catch(() => {});
    track("open");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!state?.ready || lead) return;
    const t = setInterval(() => {
      api.get("/public/demo/latest-lead", { params: { since } }).then(({ data }) => {
        if (data.lead) {
          setLead(data.lead);
          track("lead");
        }
      }).catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, [state, lead, since, track]);

  const restart = () => {
    setLead(null);
    setSince(new Date().toISOString());
    setFrameKey((k) => k + 1);
    tracked.current.delete("lead");
  };

  const hero = state?.hero;
  const tourQs = `qr=1&ts=${encodeURIComponent(sid)}${token ? `&tour=${encodeURIComponent(token)}` : ""}`;
  const shopperUrl = hero ? `/v/${hero.id}?${tourQs}` : "#";
  const lotUrl = state ? `/d/${state.dealer.slug}${token ? `?tour=${encodeURIComponent(token)}` : ""}` : "#";
  const accent = brand?.accent_color || "#0A8CF2";

  return (
    <div className="min-h-screen bg-[#05070C] text-white grid-noise">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-[#1C2233]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <VinconexLogo variant="wordmark" className="h-6 w-auto" />
          <nav className="flex items-center gap-2">
            <Link to="/contact" className="hidden sm:block text-sm text-slate-300 hover:text-white px-3 py-2" data-testid="try-contact-link">Talk to us</Link>
            <Link to="/signup"><Button data-testid="try-signup-btn" className="bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold">Get my store</Button></Link>
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <div className="max-w-2xl">
          {brand ? (
            <div className="flex items-center gap-3 mb-4" data-testid="try-brand-chip">
              {brand.logo_url && <img src={brand.logo_url} alt="" className="h-8 w-auto max-w-[120px] object-contain" />}
              <span className="inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-mono-tech uppercase tracking-widest"
                    style={{ borderColor: `${accent}80`, color: accent }}>
                Prepared for {brand.dealer_name}{brand.where ? ` · ${brand.where}` : ""}
              </span>
            </div>
          ) : (
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A8CF2] font-mono-tech mb-3">Live demo · nothing to install</div>
          )}
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
            {brand ? <>See it running as<br />{brand.dealer_name}.</> : <>Try it the way<br />your shoppers will.</>}
          </h1>
          <p className="mt-5 text-base text-slate-400">
            {brand
              ? `Every page below carries your name — the cars are ours for now. Scan the sticker, ask for the price, and watch the lead land in the dealership's CRM. Sixty seconds, no signup.`
              : `This is a real Vinconex store — ${state?.vehicles?.length || 10} cars, two rooftops, live lead walls. Scan the code, ask for the price, and watch your own lead land in the dealership's CRM below. Sixty seconds, no signup.`}
          </p>
        </div>

        {!state ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#0A8CF2]" /></div>
        ) : !state.ready ? (
          <div className="mt-10 rounded-xl border border-dashed border-[#2A2E3D] bg-[#0B0D14] p-8 text-center" data-testid="try-unavailable">
            <Building2 className="h-8 w-8 mx-auto mb-3 text-slate-600" />
            <p className="text-slate-400">The demo store is being set up. <Link to="/contact" className="text-[#7FC3FF] hover:text-white">Ask us for a walkthrough</Link> in the meantime.</p>
          </div>
        ) : (
          <div className="mt-10 grid lg:grid-cols-[420px_1fr] gap-8 lg:gap-12 items-start">
            {/* the shopper side */}
            <div className="lg:sticky lg:top-24">
              <div className="hidden sm:block mx-auto w-[400px] max-w-full rounded-[2rem] border border-[#2A2E3D] bg-[#0B0D14] p-3 shadow-2xl" data-testid="try-phone-frame">
                <div className="h-6 flex items-center justify-center">
                  <span className="h-1.5 w-16 rounded-full bg-[#2A2E3D]" />
                </div>
                <iframe
                  key={frameKey}
                  src={shopperUrl}
                  title="Vinconex shopper page"
                  onLoad={() => track("viewed")}
                  data-testid="try-iframe"
                  className="w-full h-[620px] rounded-[1.4rem] bg-black border border-[#1C2233]"
                />
              </div>

              <div className="sm:hidden rounded-xl border border-[#2A2E3D] bg-[#0B0D14] p-5 text-center" data-testid="try-mobile-open">
                <img src={hero.qr} alt="Scan the demo vehicle" className="h-40 w-40 mx-auto rounded-lg bg-white p-1" />
                <a href={shopperUrl} onClick={() => track("viewed")} className="mt-4 inline-flex">
                  <Button className="h-11 bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold" data-testid="try-open-shopper-mobile">
                    <Smartphone className="mr-2 h-4 w-4" /> Open the shopper page
                  </Button>
                </a>
                <p className="text-xs text-slate-500 mt-3">Come back to this tab afterwards — the lead appears below.</p>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-center gap-4">
                <a href={shopperUrl} target="_blank" rel="noreferrer" onClick={() => track("viewed")} data-testid="try-open-shopper" className="text-sm font-semibold text-[#7FC3FF] hover:text-white inline-flex items-center gap-1.5">
                  Open full page <ExternalLink className="h-3.5 w-3.5" />
                </a>
                <a href={lotUrl} target="_blank" rel="noreferrer" data-testid="try-open-dealer" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-1.5">
                  See the whole lot <ExternalLink className="h-3.5 w-3.5" />
                </a>
                <button type="button" onClick={restart} data-testid="try-restart" className="text-sm text-slate-500 hover:text-white inline-flex items-center gap-1.5">
                  <RotateCcw className="h-3.5 w-3.5" /> Start over
                </button>
              </div>

              <div className="mt-4 hidden sm:flex items-center gap-3 rounded-xl border border-[#1C2233] bg-[#0B0D14] p-3">
                <img src={hero.qr} alt="Scan with your phone" className="h-20 w-20 rounded bg-white p-0.5" data-testid="try-qr" />
                <p className="text-xs text-slate-400">Prefer your own phone? Scan this — it's the exact code that would be on that car's window,
                  pointing at <span className="text-slate-300">{hero.name}</span>, stock {hero.stock_number}.</p>
              </div>
            </div>

            {/* the dealer side */}
            <div>
              <ol className="space-y-5" data-testid="try-steps">
                {STEPS.map((s, i) => (
                  <li key={s.title} className="flex gap-4" data-testid={`try-step-${i + 1}`}>
                    <span className="mt-0.5 h-8 w-8 shrink-0 rounded-lg border border-[#0A8CF2]/40 bg-[#0A8CF2]/10 flex items-center justify-center font-mono-tech text-xs font-bold text-[#7FC3FF]">{i + 1}</span>
                    <div>
                      <h2 className="font-heading text-base md:text-lg font-bold flex items-center gap-2"><s.icon className="h-4 w-4 text-[#0A8CF2]" /> {s.title}</h2>
                      <p className="text-sm text-slate-400 mt-1">{s.body}</p>
                    </div>
                  </li>
                ))}
              </ol>

              <div className="mt-8">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-heading text-base md:text-lg font-bold">{brand ? `${brand.dealer_name}'s side` : "The dealership's side"}</h2>
                  <Badge variant="outline" className="border-slate-700 text-slate-400 text-[10px] font-mono-tech">{state.dealer.stores.join(" · ")}</Badge>
                </div>
                <LeadReveal lead={lead} />
              </div>

              <div className="mt-8 rounded-xl border border-[#2A2E3D] bg-gradient-to-br from-[#0A8CF2]/10 to-transparent p-6" data-testid="try-cta">
                <h2 className="font-heading text-xl md:text-lg font-extrabold">{brand ? `Put this on ${brand.dealer_name}'s inventory` : "Put this on your own inventory"}</h2>
                <p className="text-sm text-slate-300 mt-2">
                  We sync your website, print the stickers for every car you own, and route each lead to the right rooftop's CRM.
                  Most stores are live the same week.
                </p>
                <div className="flex flex-wrap gap-3 mt-4">
                  <Link to={`/contact?from=try${token ? `&ref=${encodeURIComponent(brand?.dealer_name || token)}` : ref ? `&ref=${encodeURIComponent(ref)}` : ""}`} onClick={() => track("cta")}>
                    <Button className="h-11 bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold" data-testid="try-cta-contact">
                      Book a 15-minute walkthrough <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/signup" onClick={() => track("cta")}>
                    <Button variant="outline" className="h-11 border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200" data-testid="try-cta-signup">
                      Start my store
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        <footer className="mt-16 pt-8 border-t border-[#1C2233] flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-600" data-testid="try-owner-line">{BRAND.name} · {BRAND.owner} · demo cars, not a live dealership</p>
          <LegalLinks />
        </footer>
      </main>
    </div>
  );
}
